/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import {
  connectionColumns as baseColumns,
  InternetConnection,
  shapeInternetData,
} from "./InternetData";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";

import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import {
  fetchConnectionList,
} from "./requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import { makeInternetFields } from "./internetFieldSpec";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";

interface MainInternet {
  text: string;
}

const MainInternet: React.FC = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [ipxList, setIpxList] = useState<InternetConnection[]>([]);
  const overlaySpinner = useProcessingOverlay(isFetching);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);


  const internetFields = useMemo(
    () => makeInternetFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const normalized = useMemo(
    () => normalizeFilters<InternetConnection>(activeFilters, internetFields, {
      defaultStringToContains: true,
      quotedStringsExact: true,
    }),
    [activeFilters, internetFields]
  );

  const predicate = useMemo(
    () => buildPredicate<InternetConnection>(normalized, internetFields),
    [normalized, internetFields]
  );

  const filteredData = useMemo(
    () => ipxList.filter(predicate),
    [ipxList, predicate]
  );

  const suggestedKeys = useMemo(
    () => internetFields.map(f => f.label),
    [internetFields]
  );
  console.log("normalized", normalized);


  // const columns = baseColumns.map((col) => {
  const columns = useMemo<GenericTableColumn<InternetConnection>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: InternetConnection) => row.customer?.name ?? "",
        renderCell: (row: InternetConnection) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    return cols.map((col) => {
      // 1. Override the “Service ID” column to navigate on click
      if (col.header === "Connection ID") {
        return {
          ...col,
          renderCell: (row: InternetConnection) => (
            <button
              onClick={() => {
              // CASE A: Partner view (showing ALL customers)
							//   → pick row.customerId (e.g. 11) instead of tenantId
							// CASE B: Customer view (only seeing your own)
							//   → use the Redux‐provided tenantId
              const idFromRow = Number(row.customer?.id ?? NaN);
              const nextTenantId =
                isViewingAllCustomers && Number.isFinite(idFromRow)
                  ? idFromRow
                  : tenantId;
                  navigate(
                    `/kontrol/internet/internet-details?connectionId=${row.id}`,
                    {
                      state: {
                        connectionDetail: row,
                        tenantOverride: nextTenantId,
                      },
                    }
                  );
              }}
              className="
						hover:text-semantic-text-brand 
						hover:underline
						focus:underline 
						active:underline
						font-medium
						underline
						text-semantic-text-brand
            "
            >
              {row.subscriptionId}
            </button>
          ),
        };
      }
      // 2. Override the “Parent connection” column to navigate on click
      if (col.header === "Parent Connection") {
        return {
          ...col,
          renderCell: (row: InternetConnection) => {
            const label = row.parentConnection || "—";
            const id = row.parentConnectionId;
      
            if (!id || id === "-") {
              return <span className="text-semantic-text-secondary">-</span>;
            }
      
            // Prefer the explicit subid we shaped; fallback: parse from label "[TYPE] xyz"
            const parsedFromLabel =
              label.includes("]") ? label.split("]").slice(1).join("]").trim() : "";
            const subid = row.parentConnectionSubid ?? parsedFromLabel ?? "";
      
            const onClick = () => {
              const qs = new URLSearchParams({
                portId: String(id),
                ...(subid ? { subid } : {}),
              }).toString();
              navigate(`/kontrol/port-details?${qs}`);
            };
      
            return (
              <button
                onClick={onClick}
                className="
                  hover:text-semantic-text-brand 
                  hover:underline
                  focus:underline 
                  active:underline
                  font-medium
                  underline
                  text-semantic-text-brand
                "
              >
                {label}
              </button>
            );
          },
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetching(true);
        const response = await fetchConnectionList(api, tenantId);
        const { status, data } = response;
        if (status >= 500 || data.success === false) {
          setFetchError(data.message || "Unknown server error");
          setIpxList([]);
        }
        //Valid array payload
        else if (Array.isArray(data.data)) {
          setIpxList(shapeInternetData(data.data));
        }
        //Some other unexpected shape
        else {
          setIpxList([]);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.error("Error fetching table ports", err);
        setFetchError(err.message || "Network error");
        setIpxList([]);
      } finally {
        setIsFetching(false);
        setHasFetched(true);
      }
    };
    fetchData();
  }, [getAccessTokenSilently, tenantId]);

  //Cases to handle the fetching and the displat of empty items
  // If data is being fetched, show only the overlay spinner.
  if (!hasFetched || isFetching) {
    return overlaySpinner;
  }
  //If there's an error in the request
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  //If no ports are returned, display the empty state.
  if (ipxList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
        >
          <div className="w-[520px] m-5">
            <GenericSearchbar
              placeholder='Find by attribute or value'
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
              liveFiltering
              debounceMs={200}
            />
          </div>
          <div className="px-5 mb-5">
            <GenericTable
              columns={columns}
              data={filteredData}
              initialRowsPerPage={10}
              rowsPerPageOptions={[10, 20, 30]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
            />
          </div>
        </GenericCard>
      </div>
    </div>
  );
};

export default MainInternet;
